import React, {useContext, useRef} from 'react';
import {useSHCore, AjaxParam} from '@hooks/useSHCore';
import {getInitialComponentData} from "@helpers/HelperInitialPageState";

const ContextTLD = React.createContext({});

/**
 * @function ContextTLDProvider
 * @property {{tldAll:Array<TLDData>, tldCat: Array<TLDCategory>, tldTag: Array<TLDTags>, defaultTag: TLDTags}} contextData
 * @property {Function} getAllTLDs Връща всички TLD-та
 * @property {Function} getForCategory Връща TLD-та от дадена категория
 * @property {Function} getAllTLDCategories Връща всички TLD категории
 * @property {Function} getAllTags Връща всички TLD тагове
 * @property {Function} getDefaultTag Връща всички TLD таг-а по подразбиране
 *
 */
const ContextTLDProvider = ({children}) => {

	const providerData = getInitialComponentData('contextTLDProvider');

	/**
	 * Отделен масив с TLD-та, зареждани само за валидация (за да няма merge конфликти с contextData.tldAll).
	 */
	const loadedTLDForValidateRef = useRef([]);

	/**
	 * Връща всички TLD-та
	 * @returns {Array<TLDData>}
	 */
	const getAllTLDs = () => {
		return core.state.contextData.tldAll;
	};

	/**
	 * Loads (AJAX) all TLDs for a given category and saves them to contextData.tldAll.
	 * Optionally, it accepts a callback so that we can then load the prices via priceContext (loadPricesForServiceIds).
	 *
	 * @param {string} categoryId
	 * @param {Function} callBackSuccess
	 * @param {Function} callBackError
	 */
	const getAllTLDsForCategory = (categoryId, callBackSuccess = null, callBackError = null) => {
		const ajaxParam = new AjaxParam();

		ajaxParam.callbackSuccess = (data) => {
			const tldAll = data?.componentData?.tldAll || [];

			core.setState((prev) => ({
				...prev,
				contextData: {
					...(prev.contextData || {}),
					tldAll
				}
			}), () => {
				if (typeof callBackSuccess === 'function') {
					callBackSuccess(tldAll, data);
				}
			});
		};

		ajaxParam.callbackError = (err) => {
			if (typeof callBackError === 'function') {
				callBackError(err);
			}
		};

		const actionData = {
			categoryId: categoryId
		};

		core.ajaxCall('ActionGetTLDsForCategory', actionData, ajaxParam);
	};

	/**
	 * Връща всички TLD tagowe
	 * @returns {Array<TLDTags>}
	 */
	const getAllTags = () => {
		return core.state.contextData.tldTag;
	};
	/**
	 * Връща всички TLD таг-а по подразбиране
	 * @returns {TLDTags}
	 */
	const getDefaultTag = () => {
		return core.state.contextData.defaultTag;
	};
	/**
	 * Връща всички TLD категории
	 * @returns {Array<TLDCategory>}
	 */
	const getAllTLDCategories = () => {
		return core.state.contextData.tldCat;
	};

	/**
	 * Проверява и добавя ако липсва конкретно TLD в контекста
	 *
	 * @param {string} tldCode
	 * @param {boolean} forValidate
	 * @param {Function} callBackSuccess
	 * @param {Function} callBackError
	 * @returns void
	 */
	const loadTLDByCode = (tldCode, forValidate = false, callBackSuccess = null, callBackError = null) => {
		const existingTLD = getTLDByCode(tldCode, forValidate);

		if (existingTLD) {

			if (typeof callBackSuccess === 'function') {
				callBackSuccess(existingTLD);
			}

		} else {

			loadTLDByCodeAjax(tldCode, forValidate, callBackSuccess, callBackError);

		}
	};

	/**
	 * Зарежда от бекенда конкретно TLD по code и го merge-ва в съответния масив
	 *
	 * @param {string} tldCode
	 * @param {boolean} forValidate
	 * @param {Function} callBackSuccess
	 * @param {Function} callBackError
	 * @returns void
	 */
	const loadTLDByCodeAjax = (tldCode, forValidate = false, callBackSuccess = null, callBackError = null) => {
		if (!tldCode) {
			if (typeof callBackError === 'function') {
				callBackError('DOMAIN_TLD_INVALID_ERROR');
			}
			return;
		}

		const ajaxParam = new AjaxParam();

		// On success
		ajaxParam.callbackSuccess = (data) => {
			const tldData = data?.componentData?.tldData || [];

			if (forValidate) {

				// Add tlds to loadedTLDForValidate array (validate functions use this array)
				const combined = [...loadedTLDForValidateRef.current, ...tldData];

				tldData.forEach((item) => {
					const code = String(item?.code || '').toLowerCase();

					if (code) {
						loadedTLDForValidateRef.current = combined.filter((item, index, arr) => {
							if (!item?.code) {
								return false;
							}

							const currentCode = String(item.code).toLowerCase();
							return arr.findIndex((x) => String(x?.code || '').toLowerCase() === currentCode) === index;
						});
					}
				});

				if (typeof callBackSuccess === 'function') {
					callBackSuccess(tldData, data);
				}

			} else {

				mergeTLDs(tldData, () => {
					if (typeof callBackSuccess === 'function') {
						callBackSuccess(tldData, data);
					}
				});

			}
		};

		// On error
		ajaxParam.callbackError = (err) => {
			if (typeof callBackError === 'function') {
				callBackError(err);
			}
		};

		// Ajax params
		const actionData = {
			tldCode: tldCode
		};

		// Ajax call
		core.ajaxCall('ActionLoadTLDByCode', actionData, ajaxParam);
	};

	/**
	 * Проверява дали дадено TLD съществува в съответния масив
	 *
	 * @param {string} tldCode
	 * @param {boolean} forValidate
	 * @returns {TLDData|null}
	 */
	const getTLDByCode = (tldCode, forValidate = false) => {
		if (!tldCode) {
			return null;
		}

		const normalizedCode = String(tldCode).toLowerCase();

		if (forValidate) {
			// Copy currently loaded tls from tldAll to loadedTLDForValidate (if not already done),
			// because validate functions use loadedTLDForValidate array, which is separate from contextData.tldAll
			// (to avoid merge conflicts with the rest of the app)
			const allTLD = Array.isArray(core.state.contextData?.tldAll)
			  ? core.state.contextData.tldAll
			  : [];

			const combined = [...allTLD, ...loadedTLDForValidateRef.current];

			const deduped = combined.filter((item, index, arr) => {
				if (!item?.code) {
					return false;
				}

				const currentCode = String(item.code).toLowerCase();

				return arr.findIndex((x) =>
				  String(x?.code || '').toLowerCase() === currentCode
				) === index;
			});

			loadedTLDForValidateRef.current = deduped;
			return loadedTLDForValidateRef.current.find((item) => String(item?.code || '').toLowerCase() === normalizedCode) || null;
		}

		const allTLD = getAllTLDs() || [];

		return allTLD.find((item) => String(item?.code).toLowerCase() === normalizedCode) || null;
	};

	/**
	 * Merge-ва подадени TLD-та в contextData.tldAll по code
	 *
	 * @param {Array<TLDData>} partialTLDs
	 * @param {Function} callback
	 * @returns void
	 */
	const mergeTLDs = (partialTLDs, callback = null) => {
		if (!Array.isArray(partialTLDs) || partialTLDs.length === 0) {
			if (typeof callback === 'function') {
				callback(core.state.contextData?.tldAll || []);
			}
			return;
		}

		core.setState((prev) => {
			const prevTLDs = Array.isArray(prev?.contextData?.tldAll)
			  ? prev.contextData.tldAll
			  : [];

			// Събираме старите + новите
			const combinedTLDs = [...prevTLDs, ...partialTLDs];

			// Оставяме само по 1 елемент за всеки code
			const nextTLDs = combinedTLDs.filter((item, index, arr) => {
				if (!item?.code) {
					return false;
				}

				const currentCode = String(item.code).toLowerCase();

				return arr.findIndex((x) => {
					return String(x?.code || '').toLowerCase() === currentCode;
				}) === index;
			});

			return {
				...prev,
				contextData: {
					...(prev.contextData || {}),
					tldAll: nextTLDs
				}
			};
		}, () => {
			if (typeof callback === 'function') {
				callback(core.state.contextData?.tldAll || []);
			}
		});
	};

	/**
	 * Проверка за валидно име на домейн.
	 * Валидното име на домейн е:
	 * - Изписано само с латиница (за всички TLD-та)
	 * - Изписано само на кирилица (за определени TLD-та)
	 * - XN име (започващо с XN--)
	 * Невалидното име на домейн:
	 * - Изписано едновременно на латиница/кирилица.
	 * - Започващо/завърщващо с тире.
	 * - Невалидно XN име (което не се транслира правилно към кирилица).
	 **/
	const validateDomainName = (domainNameFull, isTransfer) => {
		// Валидира име на домейн.

		/**
		 * https://jira.superhosting.bg/browse/ORDER-35
		 */
		domainNameFull = domainNameFull.replace(/^www\./, '');
		domainNameFull = domainNameFull.toLowerCase().trim();

		const arrDomainData = domainNameFull.split('.');
		const domainName = arrDomainData[0];
		const domainTld = domainNameFull.substring(domainName.length + 1, domainNameFull.length);

		// ПЪРВО ПРАВИМ ВАЛИДАЦИИ НА САМОТО ИМЕ
		////////////////////////////////////////////
		////////////////////////////////////////////
		/**
		 * https://pi.superhosting.bg/public/index.php?path_info=projects%2Fanglijska-versiya-testvane%2Ftasks%2F53
		 * Поне два символа
		 */
		if (domainName.length <= 2) {
			throw 'DOMAIN_NAME_LENGTH_ERROR';
		}
		////////////////////////////////////////////
		////////////////////////////////////////////
		const t = domainNameFull.replace(' ', '');
		if (t !== domainNameFull) {
			throw 'DOMAIN_NAME_INVALID_ERROR';
		}
		////////////////////////////////////////////
		////////////////////////////////////////////
		const domTmp = domainName.replace(/[.`!,?<>/()*@#%]/gi, '');
		if (domTmp !== domainName) {
			throw 'DOMAIN_NAME_DISALLOWED_ERROR';
		}
		////////////////////////////////////////////
		////////////////////////////////////////////
		// Валидира името на домейна, ако името на домейна е с латински букви.
		const dNum = domainName.replace(/[0123456789-]/gi, '');
		const dEn = domainName.replace(/[abcdefghijklmnopqrstuvwxyz0123456789-]/gi, '');
		const dBg = domainName.replace(/[абвгдежзийклмнопрстуфхцчшщъыьэюя0123456789-]/gi, '');
		if (dNum === '') {
			return;
		}
		////////////////////////////////////////////
		////////////////////////////////////////////
		/*Проверка дали съдържа едновременно и БГ символи и EN символи.*/
		if ((dEn !== '') && (dBg !== '')) {
			throw 'DOMAIN_NAME_INVALID_CYR_LAT_ERROR';
		}
		////////////////////////////////////////////
		////////////////////////////////////////////
		// Проверка дали името започва или завършва с тире.
		const dTmp = domainName.replace(/^-+|-+$/g, '');
		if (dTmp !== domainName) {
			throw 'DOMAIN_NAME_HYPHEN_ERROR';
		}
		////////////////////////////////////////////
		////////////////////////////////////////////

		if (!domainTld) {
			return;
		}

		// СЛЕД ТОВА ПРАВИМ ВАЛИДАЦИИ АКО ИМА И TLD
		////////////////////////////////////////////
		////////////////////////////////////////////
		const tldData = loadedTLDForValidateRef.current.find((item) => String(item?.code).toLowerCase() === String(domainTld).toLowerCase()) || null;

		if (typeof tldData === 'undefined' || !tldData) {
			throw 'DOMAIN_TLD_INVALID_ERROR';
		}

		if (parseInt(tldData.is_active) === 0) {
			throw 'DOMAIN_TLD_INVALID_ERROR';
		}

		if (parseInt(isTransfer) === 1 && parseInt(tldData.transferable) === 0) {
			throw 'DOMAIN_TLD_INVALID_ERROR';
		}

		/**
		 * https://ps.superhosting.bg/projects/350?modal=Task-22909-350
		 * Проверява се дали даденото TLD има изискване името на домейна да е само на кирилица
		 */

		if (parseInt(tldData.rxn) === 1) {
			const domTmpTLD = domainName.replace(/[абвгдежзийклмнопрстуфхцчшщъыьэюя0123456789-]/gi, '');
			if (domTmpTLD !== '') {
				throw 'DOMAIN_NAME_LANG_ERROR';
			}
		}

		let allowXN = (parseInt(tldData.axn) === 1);
		if (parseInt(isTransfer) === 1) {
			allowXN = (parseInt(tldData.axnt) === 1);
		}
		const isXN = (domainName.substr(0, 4) === 'xn--');
		const registrar = tldData.regs;
		if (isXN && !allowXN) {
			throw 'DOMAIN_NAME_XN_ERROR';
		}
		if ((dBg === '') && !allowXN) {
			throw 'DOMAIN_NAME_XN_ERROR';
		}

		/**
		 * Ако домейна на кирилица и registrar-а е Регистър.БГ. се проверява
		 * дали в името на домейна има поне 1 буква, която да не прилича на латинското
		 * си изписване:
		 * Когато ИНТЕРНЕТ ИМЕТО се изписва на кирилица (IDN), то трябва да съдържа букви
		 * само от кирилицата и да включва поне една отличаваща се визуално буква спрямо
		 * латинската азбука (големи и/или малки букви): б, г, д, ж, и, й, л, п, ф, ц, ч, ш, щ, ъ, ь, ю, я.
		 * https://www.register.bg/user/static/rules/bg/index.html
		 */
		if (registrar === 'RBG' || (isTransfer && registrar === 'IBGCYR')) {
			// Дали домейна е на кирилица:
			if ((dBg === '') && (dNum !== '')) {
				const idnBG = domainName.replace(/[бгджийлпфцчшщъьюя]/gi, '');

				if (idnBG === domainName) {
					throw 'DOMAIN_NAME_IDN_BG_LETTER_ERROR';
				}
			}
		}
	};

	const validateTLDForTransfer = (tld) => {
		const tldData = loadedTLDForValidateRef.current.find((item) => String(item?.code).toLowerCase() === String(tld).toLowerCase()) || null;

		if (typeof tldData !== 'undefined' && tldData) {
			if (parseInt(tldData.altr) === 1) {
				return;
			}
			throw 'DOMAIN_TLD_INVALID_ERROR';
		}
		throw 'DOMAIN_TLD_INVALID_ERROR';
	}

	const submitSearchDomainForm = (
	  event,
	  domain,
	  callBackSuccess = null,
	  callBackError = null
	) => {
		event.preventDefault();

		const target = event.target;

		domain = domain.replace('www.', '');
		if (domain === '') {
			if (typeof callBackError === 'function') {
				callBackError('EMPTY_DOMAIN_NAME_ERROR');
			}
			return;
		}

		domain = domain.toLowerCase().trim();
		const arrDomainData = domain.split('.');
		const domainName = arrDomainData[0];
		let domainTld = domain.slice(domainName.length + 1);

		if (domainTld === '') {
			domainTld = 'com';
		}

		loadTLDByCode(
		  domainTld,
		  true,
		  () => {
			  try {
				  validateDomainName(domainName + '.' + domainTld, false);

				  if (typeof callBackSuccess === 'function') {
					  callBackSuccess();
				  }

				  target.action = '/web-hosting-order-domain-results.php';
				  target.submit();
			  } catch (err) {
				  if (typeof callBackError === 'function') {
					  callBackError(err);
				  }
			  }
		  },
		  (err) => {
			  if (typeof callBackError === 'function') {
				  callBackError(err || 'DOMAIN_TLD_INVALID_ERROR');
			  }
		  }
		);
	};

	const core = useSHCore({
		name: providerData.componentName,
		token: providerData.componentToken,
		initialState: {
			contextData: providerData.componentData,
			getAllTLDs,
			getAllTLDCategories,
			getAllTags,
			getDefaultTag,
			getAllTLDsForCategory,
			mergeTLDs,
			getTLDByCode,
			loadTLDByCode,
			loadTLDByCodeAjax,
			validateDomainName,
			validateTLDForTransfer,
			submitSearchDomainForm,
		},
	});

	return (
	  <ContextTLD.Provider value={core.state}>
		  {children}
	  </ContextTLD.Provider>
	);
};

export {ContextTLD, ContextTLDProvider};

/**
 *
 * @returns {ContextServicePriceProvider}
 */
export const useTLDContext = () => useContext(ContextTLD);


/**
 * @typedef {Object} TLDData
 * @property {string} id - ID на TLD-то
 * @property {string} code - Кодът на TLD-то. Показва се в сайта.
 * @property {number} priority - Приоритет на TLD-то. Използва се за подредба.
 * @property {boolean} has_special_page - Указва дали дадено TLD има детайлна страница.
 * @property {boolean} transferable - Указваща дали TLD-то може да се трансферира
 * @property {string} transfer_id - ID на услугата за трансфер
 * @property {string} category - Име на категория
 * @property {string} category_id - ID на категорията
 * @property {number} category_order - подредба в категорията
 * @property {Array.<TLDLabelData>} labels - Label-и за дадено TLD
 */


/**
 * @typedef {Object} TLDCategory
 * @property {string} id - ID на Категорията
 * @property {string} name - Име на категорията.
 * @property {string} anchor - Код, който се използва за закотвяне на страницата.
 * @property {number} order - Подредба
 * @property {boolean} isNew - Дали е нова категория
 * @property {boolean} isDefault - Дали е категория по подразбиране
 * @property {boolean} isActive - Дали е активна категория
 */


/**
 * @typedef {Object} TLDTags
 * @property {string} id - ID на Таг-а
 * @property {string} name - Име на таг.
 * @property {number} order - Подредба.
 * @property {string} anchor - Котва в страницата
 * @property {boolean} active - Дали е активен
 * @property {boolean} default - Дали е категория по подразбиране
 */


/**
 * @typedef {Object} TLDLabelData
 * @property {string} tld - Указва към кое тлд е label-а.
 * @property {string} background_color - Цвят на фона на label-а
 * @property {string} name - Текст на label-а.
 * @property {string} text_color - Цвят на текста на label-а.
 */
